import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cookie from 'react-cookie';
import { canUseDOM } from 'exenv';

import bwLogo from '../../public/bwlogo.png';
import bwAppExample from '../../public/bwappexample.png';

const COOKIE_NAME = 'bw_app_dl_sheet';
const COOKIE_CLOSED = 'closed';
const DISMISS_MAX_AGE_SEC = 30 * 24 * 60 * 60;

// Try the installed app first via its custom URL scheme.
// If the app is not installed, iOS won't respond and the 1.5s fallback
// timer redirects to the App Store instead.
const APP_SCHEME_BASE = 'bw://';
const APP_STORE_FALLBACK_URL = 'https://apps.apple.com/app/apple-store/id686664992';
const APP_OPEN_FALLBACK_MS = 1500;

// Detect iOS / iPadOS on the client. Covers iPhone, iPod, legacy iPads, and
// modern iPadOS 13+ Safari (which spoofs a MacIntel UA but still reports touch
// support). The server UA gate can't see that last case on its own.
function isIOSClient() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const { userAgent, platform, maxTouchPoints } = navigator;
  if (/iPhone|iPad|iPod/i.test(userAgent || '')) {
    return true;
  }
  if (platform === 'MacIntel' && typeof maxTouchPoints === 'number' && maxTouchPoints > 1) {
    return true;
  }
  return false;
}

// Build the correct deep link based on the current page:
//   - Forecast page (/forecast/marine-weather/@lat,lon)
//       → bw://marine-forecast/@lat,lon  (opens the in-app forecast sheet)
//   - Map/charts page (/embed/charts/@lat,lon,zoomz)
//       → bw://open-map/@lat,lon,zoomz   (pans the in-app map to that location)
// Falls back to bw:// (app home) when no coordinates are present.
function buildAppSchemeURL() {
  if (typeof window === 'undefined') {
    return APP_SCHEME_BASE;
  }
  const { pathname } = window.location;

  if (pathname.indexOf('/forecast/marine-weather/') !== -1) {
    const m = pathname.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (m) {
      return `${APP_SCHEME_BASE}marine-forecast/@${m[1]},${m[2]}`;
    }
  }

  if (pathname.indexOf('/embed/charts/') !== -1) {
    const m = pathname.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*),([\d.]+)z/);
    if (m) {
      return `${APP_SCHEME_BASE}open-map/@${m[1]},${m[2]},${m[3]}z`;
    }
  }

  return APP_SCHEME_BASE;
}

const FONT_FAMILY = '"Source Sans Pro", sans-serif';
const BRAND_NAVY = '#002447';

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10049,
  background: 'rgba(0, 0, 0, 0.5)',
};

const sheetStyle = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 10050,
  background: '#fff',
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.15)',
  padding: '20px 24px calc(20px + env(safe-area-inset-bottom))',
  fontFamily: FONT_FAMILY,
  color: '#111',
};

const titleStyle = {
  margin: '4px 0 12px 0',
  textAlign: 'center',
  fontSize: 24,
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: '-0.01em',
  color: '#111',
};

const previewWrapStyle = {
  textAlign: 'center',
  lineHeight: 0,
  marginBottom: 12,
};

const previewImgStyle = {
  display: 'inline-block',
  maxWidth: '100%',
  height: 'auto',
  width: 220,
};

const dividerStyle = {
  border: 'none',
  borderTop: '1px solid #e5e7eb',
  margin: '0 0 12px 0',
};

const listStyle = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
};

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 0',
};

const iconWrapStyle = {
  flex: '0 0 auto',
  width: 44,
  height: 44,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

const appIconWrapStyle = {
  ...iconWrapStyle,
  background: BRAND_NAVY,
};

const browserIconWrapStyle = {
  ...iconWrapStyle,
  background: '#f3f4f6',
};

const iconImgStyle = {
  width: 44,
  height: 44,
  objectFit: 'cover',
};

const rowLabelStyle = {
  flex: '1 1 auto',
  minWidth: 0,
  fontSize: 17,
  fontWeight: 600,
  color: '#111',
};

const rowButtonBaseStyle = {
  flex: '0 0 auto',
  minWidth: 120,
  borderRadius: 999,
  padding: '10px 16px',
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: FONT_FAMILY,
};

const openBtnStyle = {
  ...rowButtonBaseStyle,
  background: BRAND_NAVY,
  color: '#fff',
  border: 'none',
};

const continueBtnStyle = {
  ...rowButtonBaseStyle,
  background: '#fff',
  color: '#111',
  border: '1px solid #d1d5db',
};

const closeBtnStyle = {
  position: 'absolute',
  top: 8,
  right: 8,
  border: 'none',
  background: 'transparent',
  padding: 8,
  fontSize: 22,
  lineHeight: 1,
  color: '#6b7280',
  cursor: 'pointer',
};

const GlobeIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#6b7280"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

class MobileAppDownloadSheet extends Component {
  static propTypes = {
    printVersion: PropTypes.bool,
    doTrackEvent: PropTypes.func.isRequired,
  };

  static defaultProps = {
    printVersion: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      appStoreUrl: '',
    };
    this.promoDeferOuter = null;
    this.promoDeferInner = null;
    this.appStoreFallbackTimer = null;
    this.visibilityListener = null;
  }

  componentWillUnmount() {
    if (this.promoDeferOuter != null) {
      window.clearTimeout(this.promoDeferOuter);
    }
    if (this.promoDeferInner != null) {
      window.clearTimeout(this.promoDeferInner);
    }
    this.clearAppStoreFallback();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.printVersion) {
      this.setState({ visible: false });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.visible && this.state.visible) {
      this.props.doTrackEvent('Mobile CTA Viewed', {
        ctaType: 'bottom_sheet',
        pageScreenName: window.location.pathname,
      });
    }
  }

  componentDidMount() {
    if (!canUseDOM || typeof window === 'undefined') {
      return;
    }
    if (this.props.printVersion) {
      return;
    }
    if (cookie.load(COOKIE_NAME) === COOKIE_CLOSED) {
      return;
    }
    const promo = window.__BW_APP_DOWNLOAD_PROMO__ || {};
    // Trust the server UA gate when it says show; otherwise fall back to a
    // client-side iOS check to catch iPadOS 13+ Safari (MacIntel + touch).
    const shouldShow = promo.show === true || isIOSClient();
    if (!shouldShow) {
      return;
    }
    const appStoreUrl = promo.appStoreUrl || APP_STORE_FALLBACK_URL;
    // Nested async so setState is not flagged by react/no-did-mount-set-state (allow-in-func).
    // Keeps first paint aligned with SSR before showing the sheet.
    this.promoDeferOuter = window.setTimeout(() => {
      this.promoDeferOuter = null;
      this.promoDeferInner = window.setTimeout(() => {
        this.promoDeferInner = null;
        if (this.props.printVersion) {
          return;
        }
        this.setState({ visible: true, appStoreUrl });
      }, 0);
    }, 0);
  }

  persistDismiss = () => {
    const expires = new Date(Date.now() + DISMISS_MAX_AGE_SEC * 1000);
    cookie.save(COOKIE_NAME, COOKIE_CLOSED, {
      path: '/',
      maxAge: DISMISS_MAX_AGE_SEC,
      expires,
    });
  };

  clearAppStoreFallback = () => {
    if (this.appStoreFallbackTimer != null) {
      window.clearTimeout(this.appStoreFallbackTimer);
      this.appStoreFallbackTimer = null;
    }
    if (this.visibilityListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
  };

  handleClose = () => {
    this.persistDismiss();
    this.setState({ visible: false });
    this.props.doTrackEvent('Mobile CTA Clicked', {
      action: 'browser',
      ctaType: 'bottom_sheet',
      pageScreenName: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  };

  handleOpen = (e) => {
    e.preventDefault();
    this.persistDismiss();
    this.setState({ visible: false });
    this.props.doTrackEvent('Mobile CTA Clicked', {
      action: 'app',
      ctaType: 'bottom_sheet',
      pageScreenName: typeof window !== 'undefined' ? window.location.pathname : '',
    });

    const { appStoreUrl } = this.state;
    const fallbackUrl = appStoreUrl || APP_STORE_FALLBACK_URL;

    // Fallback to the App Store if the app scheme doesn't open within 1.5s.
    // When the app IS installed, iOS switches to it and the page goes to the
    // background — visibilitychange fires and we cancel the fallback timer.
    this.clearAppStoreFallback();
    this.appStoreFallbackTimer = window.setTimeout(() => {
      this.appStoreFallbackTimer = null;
      window.location.href = fallbackUrl;
    }, APP_OPEN_FALLBACK_MS);

    this.visibilityListener = () => {
      if (document.hidden) {
        this.clearAppStoreFallback();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityListener);

    window.location.href = buildAppSchemeURL();
  };

  render() {
    if (!this.state.visible || this.props.printVersion) {
      return null;
    }
    return (
      <div>
        <div
          aria-hidden="true"
          style={backdropStyle}
          onClick={this.handleClose}
        />
        <div
          className="bw-mobile-app-download-sheet"
          role="dialog"
          aria-label="Open in the Buoyweather app"
          style={sheetStyle}
          data-testid="bw-app-download-sheet"
        >
          <button
            type="button"
            style={closeBtnStyle}
            aria-label="Dismiss"
            onClick={this.handleClose}
          >
            &times;
          </button>
          <h2 style={titleStyle}>
            Buoyweather is better
            <br />
            on the app
          </h2>
          <div style={previewWrapStyle}>
            <img
              src={bwAppExample}
              alt="Buoyweather app on an iPhone"
              style={previewImgStyle}
            />
          </div>
          <hr style={dividerStyle} />
          <ul style={listStyle}>
            <li style={rowStyle}>
              <div style={appIconWrapStyle}>
                <img src={bwLogo} alt="" style={iconImgStyle} />
              </div>
              <span style={rowLabelStyle}>Buoyweather App</span>
              <button
                type="button"
                style={openBtnStyle}
                onClick={this.handleOpen}
                data-testid="bw-app-download-sheet-open"
              >
                Open App
              </button>
            </li>
            <li style={rowStyle}>
              <div style={browserIconWrapStyle}>
                <GlobeIcon />
              </div>
              <span style={rowLabelStyle}>Browser</span>
              <button
                type="button"
                style={continueBtnStyle}
                onClick={this.handleClose}
                data-testid="bw-app-download-sheet-continue"
              >
                Continue
              </button>
            </li>
          </ul>
        </div>
      </div>
    );
  }
}

export default MobileAppDownloadSheet;



// WEBPACK FOOTER //
// ./src/components/MobileAppDownloadSheet/MobileAppDownloadSheet.js